import styled, {css} from "styled-components";
import * as style from "@COMPONENTS/style/productlist/departurepopup";
import {SelectWrap, SelectBox, SelectLabel, SelectOptions} from "@FRAMEWORK/ui/desktop/select/";
import {useState, useId, useEffect, useMemo, useCallback} from "react";
import withCalendar, {Calendar, WithCalendarRef} from "@COMPONENTS/hoc/withCalendar";
import useContentsHoliday from "@QUERIES/essential/contents/useContentsHoliday";
import dayjs, {Dayjs} from "dayjs";
import {format} from "@yb/utils";
import {EventExt} from "./EventListPopup";

interface DayProps {
	isActive?: boolean;
	CheckedDay?: boolean;
	today?: boolean;
	holiday?: boolean;
}

const CalendarBox = styled.div`
	width: 400px;
	background-color: #fff;

	table {
		th {
			height: 44px;
			background-color: #fff;
			text-align: center;
			border-top: none;
		}
		td {
			height: 77px;
			padding: 0;
			border: 1px solid var(--line-color);
			&:not(:last-child) {
				border-right-color: #eee;
			}
		}
	}
`;

const CalendarMonthWrap = styled.div`
	border-top: 1px solid var(--line-color);
	border-right: 1px solid var(--line-color);
	border-left: 1px solid var(--line-color);
`;

const CalendarMonth = styled.div`
	position: relative;
	display: flex;
	width: 177px;
	height: 70px;
	margin: 0 auto;
	justify-content: center;

	em {
		font-size: 22px;
		font-weight: bold;
		color: var(--emp-color);
		line-height: 1;
	}
	label {
		font-size: var(--medium-size);
		font-weight: normal;
	}
	ul {
		top: 50px;
		left: 50%;
		width: 245px;
		max-height: 155px;
		transform: translateX(-50%);
		> li {
			justify-content: center;
			padding: 0;
		}
	}
`;

const MonthButton = styled.button<{types?: "prev" | "next"}>`
	position: absolute;
	top: 32px;
	width: 9px;
	height: 9px;
	border-left: 1px solid var(--emp-color);
	border-bottom: 1px solid var(--emp-color);
	background-color: transparent;

	&:disabled {
		border-color: var(--bl-color);
	}

	${props =>
		props.types === "prev"
			? `
                left: 0px;
                transform: rotate(45deg);
            `
			: `
                right: 0px;
                transform: rotate(-135deg);
            `}
`;

const TableCalendar = styled.table`
	table-layout: fixed;

	th {
		&:first-child {
			border-left: 1px solid var(--line-color);
		}

		&:last-child {
			border-right: 1px solid var(--line-color);
		}
	}
`;

const Month = styled.th`
	height: 35px;
	padding: 10px;
	box-sizing: border-box;
	border-top: 1px solid var(--line-color);
	color: var(--emp-color);
	text-align: center;
	&:first-child {
		color: #ff5c46;
	}
	&:last-child {
		color: #4567ad;
	}
`;

const ProductDays = styled.td<DayProps>`
	position: relative;
	box-sizing: border-box;
	height: 35px;
	color: #aaa;
	vertical-align: top;
	text-align: left;
	cursor: initial;

	> span {
		border: 1px solid transparent;
		outline: 1px solid ${props => (props.CheckedDay ? "var(--emp-color)" : "transparent")};
		font-size: var(--basic-size);
		box-sizing: border-box;
	}

	&:first-child {
		> span {
			color: var(--red-color);
		}
	}
	&:last-child {
		> span {
			color: var(--blue-color);
		}
	}
	${props =>
		props.isActive &&
		`
        color: var(--emp-color);
        cursor: pointer;

        &:hover {
            > span {
                outline: 1px solid var(--emp-color);
            }
        }
    `}
`;

const Day = styled.span<DayProps>`
	position: relative;
	display: flex;
	padding-top: 4px;
	width: 100%;
	height: 100%;
	color: ${props => (props.holiday ? "var(--red-color)" : "")};
	align-items: start;
	justify-content: center;
	line-height: 1;
	z-index: 2;

	span {
		display: flex;
		width: 26px;
		padding: 5px 0 7px;
		align-items: center;
		justify-content: center;
		z-index: 2;
	}
	${props =>
		props.today &&
		`
			span {
				border-radius: 50%;
				background-color: var(--emp-color);
				color: #fff;
			}
		`}
`;

const ResStatus = styled.div<{state?: string; lowestPrice?: boolean}>`
	position: absolute;
	display: flex;
	bottom: 0;
	width: 100%;
	height: 40px;
	font-size: 13px;
	color: var(--default-color);
	align-items: center;
	justify-content: center;

	${props => {
		if (props.state === "possible") {
			return `
			background-color: var(--yellow-color);
		  `;
		}
		if (props.state === "waiting") {
			return `
			background-color: #bfd1f5;
		  `;
		}
		if (props.state === "deadline") {
			return `
			background-color: var(--line-color);
		  `;
		}
	}}
	${props =>
		props.lowestPrice &&
		css`
			color: #ff3e24;
		`}
`;

const CalendarSelectBox = styled(SelectBox)`
	letter-spacing: 0;
	label {
		font-size: 22px;
		font-family: "pr-font-medium";
	}

	&:before {
		right: -15px;
		top: 35px;
	}
`;

type Parameter = {
	data: {minPrice: string; eventList: Array<EventExt>};
};

type EventDay = {
	day: Dayjs;
	/** 예약상태 */
	resStatus: string;
	/** 공휴일 */
	holiday: boolean;
	/** day행사중 최저가 */
	price: number;
};

const formatPrice = prop => {
	if (prop < 10000) {
		return `${format.koKRFormatNumber(prop)}원~`;
	}
	const million = Math.floor(prop / 10000);
	return `${million}만~`;
};

const EventListCalendar = withCalendar((props: Calendar & Parameter, ref: WithCalendarRef) => {
	const {
		date,
		isNext,
		isPrevious,
		rangeMonth,
		// dayList,
		month,
		// min,
		// max,
		data,
		// clearDate,
		dateChange,
		monthChange,
		nextMonth,
		prevMonth,
	} = props;

	const id = useId();
	const [showMonth, setShowMonth] = useState(false);
	useEffect(() => {
		const event = e => {
			if (document.body.contains(e.target)) {
				!e.target.closest(`#selectWrap-${CSS.escape(id)}`) && setShowMonth(false);
				e.target.closest(`#options-${CSS.escape(id)}`) && setShowMonth(false);
			}
		};
		setTimeout(() => window.addEventListener("click", event));
		return () => {
			window.removeEventListener("click", event);
		};
	}, []);

	const eventList = data?.eventList;
	const minPrice = data?.minPrice;

	const holidayFetch = useContentsHoliday(dayjs(month).format("YYYY"), {staleTime: 1000 * 60 * 5});
	const today = dayjs();

	const groupByEvStartDt = useMemo(() => {
		const result = {};
		if (!eventList) return result;
		for (const item of eventList) {
			const outStartDt = item.outStartDt;
			if (!result[outStartDt]) {
				result[outStartDt] = [];
			}
			result[outStartDt].push(item);
		}
		return result;
	}, [eventList, month]);

	const makeEventDayList = useCallback(
		(referenceDate: Dayjs): Array<EventDay> => {
			const dayCount = referenceDate.date(1).daysInMonth();
			/** 1~7 월~일 */
			const startWeekDay = referenceDate.day();
			const endWeekDay = referenceDate.date(dayCount).day();
			const firstWeekEmptyArray = Array(startWeekDay).fill(null);
			const lastWeekEmptyArray = Array(6 - endWeekDay).fill(null);
			const dayArray = Array(dayCount)
				.fill(0)
				.map((o, index) => {
					let result = {};
					const day = referenceDate.add(index, "day");
					const dayFormat = day.format("YYYYMMDD");
					/* 공휴일 */
					const holiday = !!holidayFetch.data?.body.find(v => v.calDt === dayFormat);
					/* 최저가 */
					const priceArray: Array<number> = groupByEvStartDt[dayFormat]?.map(item => item.sumPrice) || [];
					const price = priceArray.length > 0 ? Math.min(...priceArray) : 0;
					/* can: 예약가능, wait: 예약대기, end: 예약마감 */
					const calStatusArray: Array<string> = groupByEvStartDt[dayFormat]?.map(item => item.evReserveStatus) || [];
					let resStatus = "";
					if (calStatusArray.includes("can")) {
						resStatus = "possible";
					} else if (calStatusArray.includes("wait")) {
						resStatus = "waiting";
					} else if (calStatusArray.includes("end")) {
						resStatus = "deadline";
					}

					result = {day, resStatus, price, holiday};
					return result;
				});
			return [...firstWeekEmptyArray, ...dayArray, ...lastWeekEmptyArray];
		},
		[groupByEvStartDt],
	);

	/** event day 주 단위로 삽입 */
	const eventDayList = useMemo(() => {
		const days = makeEventDayList(month.date(1));
		return Array.from({length: days.length / 7}).map(() => days.splice(0, 7));
	}, [makeEventDayList]);

	/** 첫 랜더링 디폴트 세팅 */
	useEffect(() => {
		if (month) dateChange(null);
	}, []);

	return (
		<>
			<style.ScheduleTitle>
				<h5>출발일 선택</h5>
				<style.ReservationBox>
					<style.ReservationText state="possible">예약가능</style.ReservationText>
					<style.ReservationText state="waiting">예약대기</style.ReservationText>
					<style.ReservationText state="deadline">예약마감</style.ReservationText>
				</style.ReservationBox>
			</style.ScheduleTitle>
			<CalendarBox id={id}>
				<CalendarMonthWrap>
					<CalendarMonth>
						<SelectWrap width={83} id={`selectWrap-${id}`}>
							<CalendarSelectBox types="calendar" onClick={() => setShowMonth(!showMonth)}>
								<SelectLabel isActive>{month.format("YYYY.MM")}</SelectLabel>
							</CalendarSelectBox>
							{showMonth && (
								<SelectOptions id={`options-${id}`}>
									{rangeMonth?.map((day: Dayjs, index) => (
										<li
											value={day.format("YYYYMMDD")}
											key={index}
											onClick={e => {
												monthChange(String(e.currentTarget.value));
												dateChange(null); // 선택일 초기화
											}}
										>
											{day.format("YYYY.MM")}
										</li>
									))}
								</SelectOptions>
							)}
						</SelectWrap>
						{isPrevious && (
							<MonthButton
								types="prev"
								onClick={() => {
									prevMonth();
									dateChange(null);
								}}
							>
								<span>이전달</span>
							</MonthButton>
						)}
						{isNext && (
							<MonthButton
								types="next"
								onClick={() => {
									nextMonth();
									dateChange(null);
								}}
							>
								<span>다음달</span>
							</MonthButton>
						)}
					</CalendarMonth>
				</CalendarMonthWrap>
				<TableCalendar>
					<thead>
						<tr>
							<Month>일</Month>
							<Month>월</Month>
							<Month>화</Month>
							<Month>수</Month>
							<Month>목</Month>
							<Month>금</Month>
							<Month>토</Month>
						</tr>
					</thead>
					<tbody>
						{eventDayList?.map((week, index) => (
							<tr key={index}>
								{week.map((item, weekIdx) => {
									const day = item?.day as unknown as dayjs.Dayjs;
									const price = item?.price || 0;
									const isActive = (!(day < today.add(-1, "day")) && price > 0) || !!item?.resStatus;
									return (
										<ProductDays
											isActive={isActive}
											onClick={() => {
												if (isActive) {
													const isSameDay = date?.isSame(day);
													dateChange(!isSameDay ? day : null);
												}
											}}
											key={weekIdx}
											CheckedDay={date && date.isSame(day)}
										>
											<Day today={day && today.isSame(day, "day")} holiday={item?.holiday}>
												<span>{day && day.format("DD")}</span>
											</Day>
											{price > 0 && (
												<ResStatus state={item?.resStatus || ""} lowestPrice={price === Number(minPrice)}>
													{formatPrice(price)}
												</ResStatus>
											)}
										</ProductDays>
									);
								})}
							</tr>
						))}
					</tbody>
				</TableCalendar>
			</CalendarBox>
		</>
	);
});

export default EventListCalendar;
